/**
 * Pure helper functions for crew assignment data transformations.
 * Used by CreateEditBriefing and other components that load/save assignments.
 */

/**
 * Parse the location from a custom assignment's stage_name.
 * Custom assignments encode location as "Custom - {location}" in stage_name.
 * Returns undefined if no location is encoded.
 */
export function parseLocationFromStageName(
  stageName: string | null | undefined,
  assignmentType: string | null | undefined,
): string | undefined {
  if (assignmentType === "custom" && stageName && stageName.startsWith("Custom - ")) {
    return stageName.substring("Custom - ".length);
  }
  return undefined;
}

/**
 * What to call an assignment on screen.
 *
 * The artist being filmed is the most useful label; a custom assignment
 * has a subject instead; failing both, the stage says at least where it
 * happens. The final fallback keeps "null" off the screen when a row is
 * incomplete.
 */
export function assignmentTitle(assignment: {
  artist_name?: string | null;
  subject?: string | null;
  stage_name?: string | null;
}): string {
  return (
    assignment.artist_name?.trim() ||
    assignment.subject?.trim() ||
    assignment.stage_name?.trim() ||
    "Assignment"
  );
}

/**
 * Build the assignment data object for saving to the database.
 * Ensures location is included for custom assignments.
 */
export function buildAssignmentSaveData(assignment: {
  id?: string;
  briefing_id?: string;
  crew_member_id?: string;
  artist_name?: string | null;
  subject?: string | null;
  stage_name?: string | null;
  stage_id?: string | null;
  filming_start?: string;
  filming_end?: string;
  event_date?: string | null;
  notes?: string | null;
  assignment_type?: string | null;
  location?: string | null;
  is_active?: boolean;
}): Record<string, unknown> {
  return {
    id: assignment.id || undefined,
    briefing_id: assignment.briefing_id,
    crew_member_id: assignment.crew_member_id,
    artist_name: assignment.artist_name || null,
    subject: assignment.subject || null,
    stage_name: assignment.stage_name,
    stage_id: assignment.stage_id ?? null,
    filming_start: assignment.filming_start,
    filming_end: assignment.filming_end,
    event_date: assignment.event_date,
    notes: assignment.notes || null,
    assignment_type: assignment.assignment_type || (assignment.artist_name ? "artist" : "other"),
    is_active: assignment.is_active ?? true,
    location: assignment.location || null,
  };
}
